import { AfterViewInit, Component, computed, DestroyRef, effect, inject, OnInit, signal, Signal } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { from } from 'rxjs';

import { AccountPageService } from '../../../services/account-page.service';
import { ActivatedRouteService } from 'src/app/infrastructure/services/route-service/activated-route.service';
import { ApiService } from 'src/app/infrastructure/services/api-service/api.service';
import { AppService } from 'src/app/infrastructure/services/app-services/app.service';
import { AuthService } from 'src/app/auth/services/auth.service';
import { ButtonActionComponent } from 'src/app/features/shared/controls/container/button-action/button-action.component';
import { DomainProfileService } from 'src/app/infrastructure/services/domain-profile-services/domain-profile.service';
import { FooterService } from 'src/app/features/shared/footer/footer.service';
import { HeaderService } from 'src/app/features/shared/header/services/header.service';
import { IFooterData } from 'src/app/features/shared/footer/models/footer-data';
import { IHeaderData } from 'src/app/features/shared/header/models/header-data';
import { ImageBoxComponent } from 'src/app/features/shared/image-boxes/image-box/image-box.component';
import { InputControlComponent } from 'src/app/features/shared/controls/container/input-control/input-control.component';
import { LoadingPageService } from 'src/app/infrastructure/services/loading-services/loading-page.service';
import { PageLockService } from 'src/app/infrastructure/services/loading-services/page-lock.service';
import { NotificationService } from 'src/app/features/shared/notification/notification.service';
import { SharedModule } from 'src/app/features/shared/shared.module';
import { UserStorageService } from 'src/app/features/shared/storage/services/storage-services/user-storage-service';
import { _CentralPageService } from 'src/app/features/shared/central-page/services/_central-page.service';
import { _PageService } from 'src/app/infrastructure/services/page-services/_page.service';
import { _SectionBaseComponent } from 'src/app/infrastructure/components/section-components/_section-base-component';
import { sectionLayout, TSectionLayout } from 'src/app/infrastructure/models/section-layout';
import { domainProfile } from 'src/app/infrastructure/services/domain-profile-services/models/domain-profile-data';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { loadKey } from 'src/app/infrastructure/services/loading-services/models/loadKey';
import { parseJwt } from 'src/app/features/shared/storage/functions/parse-jwt';
import { routePage } from 'src/app/infrastructure/services/route-service/models/route-page';

type TLoginViewState = 'form' | 'pending2fa';

@Component({
  selector: 'app-login-details',
  templateUrl: './login-details.component.html',
  standalone: true,
  imports: [
    SharedModule,
    InputControlComponent,
    ButtonActionComponent,
    ImageBoxComponent,
],
  styleUrls: [
        '../../../account.scss',
        './login-details.component.scss',
    ]
})
export class LoginDetailsComponent
    extends _SectionBaseComponent
    implements OnInit, AfterViewInit
{
    protected override readonly _centralPageService = injectRequired(_CentralPageService);
    protected override readonly _footerService = injectRequired(FooterService);
    protected override readonly _headerService = injectRequired(HeaderService);
    protected override readonly _loadingPageService = injectRequired(LoadingPageService);
    protected override readonly _pageService = injectRequired(_PageService);

    // Field initializer dependency: __appService must precede headerData
    private readonly __formBuilder = injectRequired(UntypedFormBuilder);
    private readonly __destroyRef = injectRequired(DestroyRef);
    private readonly __accountPageService = injectRequired(AccountPageService);
    private readonly __activatedRouteService = injectRequired(ActivatedRouteService);
    private readonly __apiService = injectRequired(ApiService);
    private readonly __authService = injectRequired(AuthService);
    private readonly __appService = injectRequired(AppService);
    private readonly __appProfileService = injectRequired(DomainProfileService);
    private readonly __notificationService = injectRequired(NotificationService);
    private readonly __pageLockService = injectRequired(PageLockService);
    private readonly __userStorageService = injectRequired(UserStorageService);

    override out_onEscape = this._out_onEscape;
    override out_onPageLoaded = this._out_onPageLoaded;

    override readonly sectionLayout$$ = signal<TSectionLayout>(sectionLayout.form);

    override template_footerLeft$$ = this._template_footerLeft$$;
    override template_sectionDetails_extraButtons$$ = this._template_sectionDetails_extraButtons$$;
    override template_sectionEdit_extraButtons$$ = this._template_sectionEdit_extraButtons$$;
    override footerData: Partial<IFooterData> = {
        showInfoBlock: true,
        hideSideBlockLeft: true,
        hideSideBlockRight: true,
    };
    override headerData: Partial<IHeaderData> = {
        sideBlockLeft_hide: true,
        sideBlockRight_hide: true,
        title: this.__appProfileService.title$$() ?? this.__appService.title$$() ?? '',
        subTitle: this.__appProfileService.title_sub$$() ?? this.__appService.sutTitle$$() ?? '',
        subTitle_hide: false,
    };

    private __form: UntypedFormGroup = this.__formBuilder.group({
        email: ['', [Validators.required, Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$')]],
        password: ['', Validators.required],
    });

    get Form() {return this.__form;}
    // Cast naar FormControl: UntypedFormGroup.controls levert AbstractControl, maar
    // app-input-control verwacht een FormControl-input (runtime zijn het FormControls).
    get FormControls() {return this.__form.controls as { [key: string]: FormControl };}

    // Tweede login-stap bij een 2FA-ingeschakeld account: alleen een code-veld, apart
    // formulier zodat de e-mail/wachtwoord-validators niet meetellen.
    private __twoFactorForm: UntypedFormGroup = this.__formBuilder.group({
        code: ['', Validators.required],
    });

    get TwoFactorForm() {return this.__twoFactorForm;}
    get TwoFactorFormControls() {return this.__twoFactorForm.controls as { [key: string]: FormControl };}

    // Na een RequiresTwoFactor-response flipt de sectie naar de code-invoerstap; geen
    // aparte route nodig (zelfde patroon als RegisterDetailsComponent's viewState$$).
    private __viewState$$ = signal<TLoginViewState>('form');
    viewState$$ = this.__viewState$$.asReadonly();
    showForm$$ = computed(() => this.viewState$$() === 'form');
    private __pendingToken: string | null = null;

    // Page-lock zolang login/2FA-verzoek loopt: knoppen (loadKeys) disabled en Enter genegeerd,
    // anders kon dezelfde code meerdere keren verstuurd worden. Bij succes blijft het lock staan
    // tot de navigatie deze pagina vernietigt (anders weer klikbaar tijdens de resolver).
    private __submitting = false;

    // Uitnodigingslink voor een e-mailadres dat al een account heeft: RegisterDetailsComponent
    // stuurt in dat geval door naar hier (?invitation=token) i.p.v. het registratieformulier te
    // tonen. Na succesvol inloggen wordt de uitnodiging alsnog geaccepteerd (__invitation_acceptIfPending),
    // hetzelfde eindresultaat als de registratie-kant al langer heeft (Invitation_Accept, zie
    // AccountController.Register) — de beheerder moet de acceptatie nog wel goedkeuren.
    private __invitationToken: string | null = null;

    isStartPage = true;
    loadKey = loadKey.loadingPage;
    loadKeys = [loadKey.loadingPage, loadKey.pageLock];
    logoPath$$ = computed(() => this.__appProfileService.logo_url$$());
    pageName_current = routePage.login;
    pageName_return = null;
    showErrors = false;

    constructor() {
        super();

        this.__destroyRef.onDestroy(() => this.__submit_end());

        // Domain-0-fallback (titel/subtitel) kan async binnenkomen ná de eerste render
        // (anoniem boot-fetch) — dus reactief bijwerken i.p.v. de eenmalige snapshot uit
        // headerData hierboven.
        effect(() => {
            const __title = this.__appProfileService.title$$() ?? this.__appService.title$$() ?? '';
            const __subTitle = this.__appProfileService.title_sub$$() ?? this.__appService.sutTitle$$() ?? '';
            this._headerService.headerData_set({ title: __title, subTitle: __subTitle });
        });
    }

    override ngOnInit() {
        super._ngOnInit();

        this.__invitationToken = this.__activatedRouteService.queryParams?.['invitation'] ?? null;

        const _authorized = this.__authService.isAuthorized();

        if (_authorized) {
            // Al ingelogd (bv. tweede tabblad) én kwam via een uitnodigingslink: alsnog accepteren
            // vóór de doorstuur, anders gaat de uitnodiging stilzwijgend verloren.
            this.__invitation_acceptIfPending();

            const gotoPage = this.__startPage_url();

            this._pageService.page_goto(gotoPage);
        } else {
            this._footerService.info_set(this.__appService?.version$$() ?? null);
            this._footerService.infoTooltip_set(this.__appService?.versionDetail$$() ?? null);
            this.emit_pageLoaded();
        }
    }

    override emit_onEscape(): void {
        super._emit_onEscape();
    }

    override emit_pageLoaded(): void {
        super._emit_pageLoaded();
    }

    // Accepteert een meegekomen uitnodigingstoken (?invitation=) na een succesvolle login/2FA
    // of bij een al-ingelogde sessie — stil falen (bv. token inmiddels verlopen/ingetrokken) mag
    // de al geslaagde login niet verstoren.
    private __invitation_acceptIfPending(): void {
        if (!this.__invitationToken) return;

        from(this.__apiService.POST('/api/Invitation/accept', {
            body: { token: this.__invitationToken },
        })).subscribe({
            // openapi-fetch verwerpt de Promise niet bij een non-ok response — next vuurt dus ook
            // bij een serverfout (bv. token inmiddels verlopen/ingetrokken). r.error onderscheidt dat
            // van een echt geslaagde acceptatie; bij een fout stil negeren (mag de al geslaagde login
            // niet verstoren), niet alsnog de succesmelding tonen.
            next: r => {
                if (r.error) return;

                this.__notificationService.pushNotification({
                    severity: 'info',
                    title: 'Uitnodiging geaccepteerd',
                    text: 'De domeinbeheerder moet je toegang nog goedkeuren.',
                });
            },
            error: () => { /* stil negeren, bv. uitnodiging inmiddels verlopen/ingetrokken */ },
        });
    }

    login() {
        if (this.__submitting) return;
        if (this.__form.invalid) {
            this.showErrors = true;
            return;
        }

        const email: string = this.FormControls.email.value;
        const password: string = this.FormControls.password.value;

        this.__submit_start();
        this.__accountPageService.login$({email, password})
            .subscribe({
                next: resp => {
                    if (resp?.requiresTwoFactor) {
                        this.__submit_end();
                        this.__pendingToken = resp.pendingToken ?? null;
                        this.showErrors = false;
                        this.__viewState$$.set('pending2fa');
                    } else {
                        // Zonder 2FA navigeert AccountPageService.login$ zelf al (tap-logica).
                        this.__invitation_acceptIfPending();
                    }
                },
                // Fout wordt centraal door de HTTP-interceptor gerapporteerd (incl. notificatie).
                error: () => this.__submit_end(),
            });
    }

    verifyTwoFactor() {
        if (this.__submitting) return;
        if (this.__twoFactorForm.invalid || !this.__pendingToken) {
            this.showErrors = true;
            return;
        }

        const code: string = this.TwoFactorFormControls.code.value;

        this.__submit_start();
        this.__accountPageService.verifyTwoFactor$(this.__pendingToken, code)
            .subscribe({
                next: () => this.__invitation_acceptIfPending(),
                // Fout (bv. ongeldige code) wordt centraal door de HTTP-interceptor gerapporteerd;
                // het formulier blijft op de code-stap staan zodat de gebruiker opnieuw kan proberen.
                error: () => this.__submit_end(),
            });
    }

    private __submit_start(): void {
        this.__submitting = true;
        this.__pageLockService.add();
    }

    private __submit_end(): void {
        if (!this.__submitting) return;

        this.__submitting = false;
        this.__pageLockService.remove();
    }

    pageLoaded_remove(): void {
        super._emit_pageLoaded();
    }

    private __startPage_url() {
        const _returnPageUrl = this._pageService.pageReturn$$()?.routePath;
        // Directe read op de guid uit het huidige token i.p.v. pageStart$$() — zelfde
        // scope-hydratie-race als AccountPageService.login$ (zie daar), hier in theorie mogelijk
        // bij een verse sessie zonder eerdere hydratie.
        const _token = this.__authService.Token;
        const _userGuid = _token ? parseJwt(_token)?.nameid ?? null : null;
        const _startPageUrl = (_userGuid ? this.__userStorageService.pageStart_forGuid(_userGuid) : null)?.routePath;

        return _returnPageUrl ?? _startPageUrl ?? routePage.home;
    }

    override ngAfterViewInit(): void {
        super._ngAfterViewInit();
    }
}
